import { breakableHtml, esc, fmt, plural, safeHref } from '../utils/formatters';
import { iconMarkup } from '../utils/icons';
import { buildBuildingDetailUrl, buildParcelDetailUrl } from '../utils/url';

/**
 * Results of „Wyszukaj działkę ewidencyjną” and „Wyszukaj budynek ewidencyjny”
 * in the filter panel (/api/egib-dzialki-search and /api/egib-budynki-search,
 * EgibMapSearchService.php).
 *
 * The answer may explain itself in `notice`: an identifier from the price
 * register that EGiB writes differently, found through the place the register
 * gives for it (`matched_by` = 'rejestr_cen'), or a unit identifier searched as
 * its building. Such items carry `register_identifier` and `register_match`
 * ('same_number' when EGiB has the same number there, 'same_place' when only the
 * place agrees), shown as a note on the card. The identifier on each card links
 * to the object's page (`detail_url`, else the address built from its public key).
 */

export const EGIB_SEARCH_LOADING_TEXT = 'Szukam w ewidencji gruntów i budynków…';

const NOT_FOUND_TEXT = 'Nie znaleziono obiektów spełniających wszystkie kryteria.';

export function egibResultCountLabel(data, scope) {
    var count = (data.items || []).length;
    if (data.total_estimated) {
        return 'Ponad ' + count + ' wyników — doprecyzuj kryteria, jeśli szukany obiekt nie jest widoczny.';
    }
    var noun = scope === 'parcels'
        ? plural(count, 'działkę', 'działki', 'działek')
        : plural(count, 'budynek', 'budynki', 'budynków');
    return 'Znaleziono ' + count + ' ' + noun + '.';
}

function message(text, modifier) {
    return '<div class="nm-egib-search-results__message' + (modifier ? ' nm-egib-search-results__message--' + modifier : '') + '"'
        + (modifier === 'notice' ? ' role="note"' : '') + '>' + esc(text) + '</div>';
}

/**
 * Where the price register points for this object: same number, or only the
 * same place. HTML: the register identifier wraps between its segments.
 */
function registerNoteHtml(scope, item) {
    var lead = item.register_match === 'same_number'
        ? 'Identyfikator w Rejestrze Cen Nieruchomości: '
        : (scope === 'parcels' ? 'Leży' : 'Stoi') + ' w miejscu zapisanym w Rejestrze Cen Nieruchomości dla identyfikatora ';
    return esc(lead) + breakableHtml(item.register_identifier);
}

// The identifier wraps between its segments in the narrow card („2198_” / „BUD”);
// its address, label and title stay plain.
function identifierHtml(scope, item) {
    var identifier = scope === 'parcels' ? item.id_dzialki : item.id_budynku;
    if (!identifier) {
        return '<code></code>';
    }
    var href = safeHref(scope === 'parcels' ? buildParcelDetailUrl(item) : buildBuildingDetailUrl(item));
    if (!href) {
        return '<code>' + breakableHtml(identifier) + '</code>';
    }
    var page = scope === 'parcels' ? 'Strona działki' : 'Strona budynku';
    return '<code><a class="ms-link" href="' + esc(href) + '" aria-label="' + esc(page + ' ' + identifier) + '"'
        + ' title="' + esc(scope === 'parcels' ? 'Otwórz stronę działki' : 'Otwórz stronę budynku') + '">'
        + breakableHtml(identifier) + '</a></code>';
}

function cardHtml(scope, item, index) {
    var title;
    var meta = [];
    if (scope === 'parcels') {
        title = 'Działka ' + (item.numer || item.id_dzialki || '');
        if (item.pole_ewid != null) {
            var roundedArea = Math.round(Number(item.pole_ewid));
            meta.push(fmt(roundedArea) + ' ' + plural(
                roundedArea,
                'metr kwadratowy',
                'metry kwadratowe',
                'metrów kwadratowych'
            ));
        }
        if (item.miejscowosc) meta.push(item.miejscowosc);
        else if (item.gmina) meta.push(item.gmina);
        if (item.obreb) meta.push('obręb ' + item.obreb);
    } else {
        title = item.rodzaj_label || 'Budynek';
        if (item.adres) meta.push(item.adres);
        if (item.miejscowosc) meta.push(item.miejscowosc);
        if (item.kondygnacje_nadziemne != null) {
            meta.push(item.kondygnacje_nadziemne + ' ' + plural(
                item.kondygnacje_nadziemne,
                'kondygnacja nadziemna',
                'kondygnacje nadziemne',
                'kondygnacji nadziemnych'
            ));
        }
    }
    return '<article class="nm-egib-result" data-egib-result-card="' + index + '">'
        + '<span class="nm-egib-result__marker" aria-hidden="true">' + (index + 1) + '</span>'
        + '<span class="nm-egib-result__body"><strong>' + esc(title) + '</strong>'
        + '<small>' + esc(meta.join(' · ')) + '</small>'
        + identifierHtml(scope, item)
        + (item.register_identifier ? '<small class="nm-egib-result__note">' + registerNoteHtml(scope, item) + '</small>' : '')
        + '</span>'
        + '<span class="nm-egib-result__actions">'
        + '<button type="button" class="nm-egib-result__preview" data-egib-preview-index="' + index + '"'
        + ' aria-label="' + esc('Pokaż na mapie: ' + title) + '" title="Pokaż na mapie bez otwierania szczegółów">'
        + iconMarkup('eye', { size: 18 })
        + '</button>'
        + '<button type="button" class="nm-egib-result__select" data-egib-select-index="' + index + '"'
        + ' aria-label="' + esc('Otwórz szczegóły: ' + title) + '" title="Otwórz pełne szczegóły">'
        + '<span aria-hidden="true">→</span>'
        + '</button>'
        + '</span>'
        + '</article>';
}

/**
 * Markup of the results box for one answer of the search.
 *
 * @param {'parcels'|'buildings'} scope
 * @param {object} data the JSON answer
 * @returns {string}
 */
export function renderEgibSearchResultsHtml(scope, data) {
    data = data || {};
    var items = data.items || [];
    if (data.error) {
        return message(data.error, 'error');
    }
    var notice = data.notice ? message(data.notice, 'notice') : '';
    if (!items.length) {
        // After a price-register lookup the notice itself says why nothing was found.
        return notice + (data.register_identifier ? '' : message(NOT_FOUND_TEXT));
    }

    return notice
        + '<div class="nm-egib-search-results__summary">' + esc(egibResultCountLabel(data, scope)) + '</div>'
        + '<div class="nm-egib-search-results__list">'
        + items.map(function(item, index) { return cardHtml(scope, item, index); }).join('')
        + '</div>';
}
